import { useEffect, useRef, useState } from "react";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, PieChart, Pie, Cell, BarChart,
} from "recharts";
import {
  Loader2, Plus, Upload, Trash2, Pencil, X, TrendingUp, Wallet, FileText,
  HardHat, PieChart as PieIcon, LineChart as LineIcon, Save, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const GOLD = "#c9a961", GREEN = "#2f7d5b", RED = "#b23a48", INK = "#1e3d3a";
const PIE = ["#1e3d3a", "#c9a961", "#2f7d5b", "#a3823f", "#6b7c3a", "#7f9b8e", "#b23a48", "#4d5a24", "#9c7b3e", "#557a6b", "#8a8f7a"];
const fmtUSD = (n) => `$${Math.round(n || 0).toLocaleString()}`;
const todayStr = () => new Date().toISOString().slice(0, 10);

const BLANK_EXP = { date: "", kind: "expense", category: "Overhead", vendor: "", amount: "", note: "" };
const BLANK_JOB = { name: "", contract: "", budget: "", spent: "", pct_complete: "", billed: "", received: "", status: "" };

const Card = ({ icon: Icon, title, right, children, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5 lg:p-6">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2 text-[#1e3d3a]">
        {Icon && <Icon className="h-4 w-4 text-[#c9a961]" />}
        <h3 className="font-head text-lg">{title}</h3>
      </div>
      {right}
    </div>
    {children}
  </div>
);

const tip = { background: "#fff", border: "1px solid #1e3d3a22", borderRadius: 12, color: INK, fontSize: 12 };

export default function PortalFinances({ money = fmtUSD }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [expForm, setExpForm] = useState(null);   // null = closed
  const [jobForm, setJobForm] = useState(null);
  const [filter, setFilter] = useState("all");
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/portal/finance/overview");
      setData(data);
    } catch { toast.error("Could not load your finances."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveExpense = async () => {
    if (!expForm.amount || Number(expForm.amount) <= 0) return toast.error("Enter an amount.");
    setBusy(true);
    try {
      const body = { ...expForm, amount: Number(expForm.amount), date: expForm.date || todayStr() };
      if (expForm.id) await api.put(`/portal/finance/expenses/${expForm.id}`, body);
      else await api.post("/portal/finance/expenses", body);
      setExpForm(null); await load();
      toast.success(expForm.id ? "Expense updated." : "Expense added.");
    } catch { toast.error("Could not save the expense."); }
    finally { setBusy(false); }
  };

  const delExpense = async (id) => {
    try { await api.delete(`/portal/finance/expenses/${id}`); await load(); toast.success("Removed."); }
    catch { toast.error("Could not remove."); }
  };

  const saveJob = async () => {
    if (!jobForm.name?.trim()) return toast.error("Give the job a name.");
    setBusy(true);
    try {
      const body = Object.fromEntries(Object.entries(jobForm).map(([k, v]) =>
        ["name", "status", "id"].includes(k) ? [k, v] : [k, Number(v) || 0]));
      if (jobForm.id) await api.put(`/portal/finance/jobs/${jobForm.id}`, body);
      else await api.post("/portal/finance/jobs", body);
      setJobForm(null); await load();
      toast.success(jobForm.id ? "Job updated." : "Job added.");
    } catch { toast.error("Could not save the job."); }
    finally { setBusy(false); }
  };

  const delJob = async (id) => {
    try { await api.delete(`/portal/finance/jobs/${id}`); await load(); toast.success("Job removed."); }
    catch { toast.error("Could not remove the job."); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (file) { e.target.value = ""; }
    if (!file) return;
    setBusy(true);
    toast.message("Reading your document with AI…");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/portal/finance/expenses/import", fd);
      await load();
      toast.success(`Added ${data.added} item${data.added === 1 ? "" : "s"} from ${data.source_file}.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not read that document.");
    } finally { setBusy(false); }
  };

  if (loading) return <div className="flex items-center gap-2 text-[#1e3d3a]/60 py-10"><Loader2 className="h-4 w-4 animate-spin" /> Loading your finances…</div>;
  if (!data) return null;

  const expenses = data.expenses || [];
  const jobs = data.jobs || [];
  const shownExpenses = filter === "all" ? expenses : expenses.filter((x) => x.category === filter && x.kind === "expense");

  return (
    <div data-testid="portal-finances" className="space-y-6">
      {data.duplicate_warnings > 0 && (
        <div data-testid="pf-dup-banner" className="flex items-start gap-3 rounded-2xl border border-[#c9a961]/50 bg-[#c9a961]/10 px-4 py-3">
          <AlertTriangle className="h-5 w-5 text-[#a3823f] shrink-0 mt-0.5" />
          <div className="text-[13px] text-[#1e3d3a]/85 leading-relaxed">
            <span className="font-semibold">{data.duplicate_warnings} {data.duplicate_warnings === 1 ? "entry" : "entries"} may be counted twice.</span>{" "}
            You’ve typed something here that also comes from your Operations tab. Rows marked <span className="font-semibold text-[#a3823f]">Possible duplicate</span> below may be double-counting — delete the one you entered here, or edit it in Operations.
          </div>
        </div>
      )}
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="pf-kpis">
        {data.kpis.map((k) => (
          <div key={k.key} data-testid={`pf-kpi-${k.key}`} className="rounded-xl border border-[#1e3d3a]/12 bg-white px-4 py-3">
            <div className="text-[9px] uppercase tracking-wide text-[#1e3d3a]/45">{k.label}</div>
            <div className="mt-1 font-head text-xl text-[#1e3d3a] tabular-nums">
              {k.unit === "$" ? money(k.value) : k.unit === "%" ? `${k.value}%` : k.value}
            </div>
          </div>
        ))}
      </div>

      {/* Trend */}
      <Card icon={TrendingUp} title="Revenue, expenses & net — last 12 months" testid="pf-trend">
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data.trend} margin={{ top: 6, right: 8, left: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e3d3a10" />
              <XAxis dataKey="label" tick={{ fill: "#1e3d3a90", fontSize: 10 }} interval={1} />
              <YAxis tick={{ fill: "#1e3d3a90", fontSize: 10 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip contentStyle={tip} formatter={(v, n) => [money(v), n]} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="revenue" name="Revenue" fill={GOLD} radius={[3, 3, 0, 0]} barSize={8} />
              <Bar dataKey="expenses" name="Expenses" fill={RED} radius={[3, 3, 0, 0]} barSize={8} />
              <Line type="monotone" dataKey="net" name="Net" stroke={GREEN} strokeWidth={2.2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Category breakdown */}
        <Card icon={PieIcon} title="Where the money goes" testid="pf-breakdown">
          {data.category_breakdown.length ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data.category_breakdown} dataKey="amount" nameKey="category" cx="42%" cy="50%" innerRadius={44} outerRadius={88} paddingAngle={2}>
                    {data.category_breakdown.map((_, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={tip} formatter={(v, n) => [money(v), n]} />
                  <Legend layout="vertical" align="right" verticalAlign="middle" wrapperStyle={{ fontSize: 10 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : <p className="text-sm text-[#1e3d3a]/50 py-10 text-center">Add expenses to see your category breakdown.</p>}
        </Card>

        {/* Quarterly projection */}
        <Card icon={LineIcon} title="Quarterly projection" testid="pf-projection"
          right={<span className="text-[10px] text-[#1e3d3a]/45">rev {data.revenue_growth_pct}% · exp {data.expense_growth_pct}%/qtr</span>}>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.quarterly_projection} margin={{ top: 6, right: 8, left: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e3d3a10" />
                <XAxis dataKey="label" tick={{ fill: "#1e3d3a90", fontSize: 10 }} />
                <YAxis tick={{ fill: "#1e3d3a90", fontSize: 10 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip contentStyle={tip} formatter={(v, n) => [money(v), n]} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="revenue" name="Revenue" fill={GOLD} radius={[3, 3, 0, 0]} />
                <Bar dataKey="expenses" name="Expenses" fill={RED} radius={[3, 3, 0, 0]} />
                <Bar dataKey="net" name="Net" fill={GREEN} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* P&L */}
      <Card icon={FileText} title="Profit & loss (tracked)" testid="pf-pnl">
        <table className="w-full text-sm">
          <tbody>
            <tr className="border-b border-[#1e3d3a]/12"><td className="py-2 font-medium">Revenue</td><td className="py-2 text-right tabular-nums font-semibold">{money(data.pnl.revenue)}</td></tr>
            {data.pnl.expenses_by_category.map((c) => (
              <tr key={c.category} className="border-b border-[#1e3d3a]/6"><td className="py-1.5 pl-4 text-[#1e3d3a]/60">{c.category}</td><td className="py-1.5 text-right text-[#1e3d3a]/70 tabular-nums">({money(c.amount)})</td></tr>
            ))}
            <tr className="border-b border-[#1e3d3a]/12"><td className="py-2 text-[#b23a48]">Total expenses</td><td className="py-2 text-right text-[#b23a48] tabular-nums font-medium">({money(data.pnl.expenses_total)})</td></tr>
            <tr><td className="py-2.5 font-semibold">Net</td><td className={`py-2.5 text-right tabular-nums font-bold text-base ${data.pnl.net >= 0 ? "text-[#2f7d5b]" : "text-[#b23a48]"}`}>{money(data.pnl.net)}</td></tr>
          </tbody>
        </table>
      </Card>

      {/* Expenses table */}
      <Card icon={Wallet} title="Expenses & income" testid="pf-expenses"
        right={
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" className="hidden" data-testid="pf-upload-input" onChange={onUpload}
              accept=".csv,.txt,.pdf,.png,.jpg,.jpeg,.webp,.xlsx,.xls" />
            <button data-testid="pf-upload-btn" disabled={busy} onClick={() => fileRef.current?.click()}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/20 px-3 py-1.5 text-xs text-[#1e3d3a]/75 hover:border-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-50 transition-colors">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload & auto-categorize
            </button>
            <button data-testid="pf-add-expense-btn" onClick={() => setExpForm({ ...BLANK_EXP, date: todayStr() })}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#2a534d] transition-colors">
              <Plus className="h-3.5 w-3.5" /> Add
            </button>
          </div>
        }>
        <div className="mb-3 flex flex-wrap gap-1.5">
          {["all", ...data.categories].map((c) => (
            <button key={c} data-testid={`pf-filter-${c}`} onClick={() => setFilter(c)}
              className={`rounded-full px-2.5 py-1 text-[10px] uppercase tracking-wide border transition-colors ${filter === c ? "border-[#c9a961] bg-[#c9a961]/15 text-[#1e3d3a]" : "border-[#1e3d3a]/12 text-[#1e3d3a]/50 hover:text-[#1e3d3a]/80"}`}>{c}</button>
          ))}
        </div>
        {shownExpenses.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[540px]">
              <thead><tr className="text-[#1e3d3a]/45 border-b border-[#1e3d3a]/10 text-xs">
                <th className="py-2 text-left font-medium">Date</th><th className="py-2 text-left font-medium">Vendor</th>
                <th className="py-2 text-left font-medium">Category</th><th className="py-2 text-right font-medium">Amount</th><th className="py-2"></th>
              </tr></thead>
              <tbody>
                {shownExpenses.map((x) => (
                  <tr key={x.id} data-testid={`pf-exp-${x.id}`} className="border-b border-[#1e3d3a]/6">
                    <td className="py-1.5 text-[#1e3d3a]/60 tabular-nums">{x.date}</td>
                    <td className="py-1.5 text-[#1e3d3a]/85">{x.vendor || "—"}{x.source === "upload" && <span className="ml-1.5 text-[9px] uppercase text-[#c9a961]">AI</span>}{x.from_ops && <span className="ml-1.5 rounded-full bg-[#1e3d3a]/8 px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-[#1e3d3a]/60">Operations</span>}{x.dup && <span title={x.dup_reason} data-testid={`pf-dup-tag-${x.id}`} className="ml-1.5 inline-flex items-center gap-0.5 rounded-full bg-[#a3823f]/15 px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-[#a3823f]"><AlertTriangle className="h-2.5 w-2.5" />Possible duplicate</span>}</td>
                    <td className="py-1.5"><span className="text-[10px] uppercase tracking-wide text-[#1e3d3a]/55">{x.kind === "income" ? "Income" : x.category}</span></td>
                    <td className={`py-1.5 text-right tabular-nums ${x.kind === "income" ? "text-[#2f7d5b]" : "text-[#1e3d3a]"}`}>{x.kind === "income" ? "+" : ""}{money(x.amount)}</td>
                    <td className="py-1.5 text-right whitespace-nowrap">
                      {x.from_ops ? (
                        <span className="text-[10px] text-[#1e3d3a]/35 pr-1">Edit in Operations</span>
                      ) : (<>
                        <button data-testid={`pf-exp-edit-${x.id}`} onClick={() => setExpForm({ ...x, amount: String(x.amount) })} className="p-1 text-[#1e3d3a]/40 hover:text-[#c9a961]"><Pencil className="h-3.5 w-3.5" /></button>
                        <button data-testid={`pf-exp-del-${x.id}`} onClick={() => delExpense(x.id)} className="p-1 text-[#1e3d3a]/40 hover:text-[#b23a48]"><Trash2 className="h-3.5 w-3.5" /></button>
                      </>)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="text-sm text-[#1e3d3a]/50 py-8 text-center">No entries yet. Add one or upload a receipt/statement to auto-categorize.</p>}
      </Card>

      {/* Jobs */}
      <Card icon={HardHat} title="Current jobs under contract" testid="pf-jobs"
        right={<button data-testid="pf-add-job-btn" onClick={() => setJobForm({ ...BLANK_JOB })}
          className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#2a534d] transition-colors"><Plus className="h-3.5 w-3.5" /> Add job</button>}>
        {jobs.length ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {jobs.map((j) => {
              const unc = Math.max(0, (j.billed || 0) - (j.received || 0));
              return (
                <div key={j.id} data-testid={`pf-job-${j.id}`} className="rounded-xl border border-[#1e3d3a]/12 bg-[#faf8f2] p-4">
                  <div className="flex items-start justify-between">
                    <div><div className="font-semibold text-[#1e3d3a]">{j.name}{j.from_ops && <span className="ml-1.5 rounded-full bg-[#1e3d3a]/8 px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-[#1e3d3a]/60 align-middle">Operations</span>}</div><div className="text-[10px] text-[#1e3d3a]/45">{j.status || "In progress"}</div></div>
                    <div className="flex gap-1">
                      {j.from_ops ? (
                        <span className="text-[10px] text-[#1e3d3a]/35">Edit in Operations</span>
                      ) : (<>
                        <button data-testid={`pf-job-edit-${j.id}`} onClick={() => setJobForm({ ...j, contract: String(j.contract), budget: String(j.budget), spent: String(j.spent), pct_complete: String(j.pct_complete), billed: String(j.billed), received: String(j.received) })} className="p-1 text-[#1e3d3a]/40 hover:text-[#c9a961]"><Pencil className="h-3.5 w-3.5" /></button>
                        <button data-testid={`pf-job-del-${j.id}`} onClick={() => delJob(j.id)} className="p-1 text-[#1e3d3a]/40 hover:text-[#b23a48]"><Trash2 className="h-3.5 w-3.5" /></button>
                      </>)}
                    </div>
                  </div>
                  <div className="mt-3 h-2 w-full rounded-full bg-[#1e3d3a]/10 overflow-hidden"><div className="h-full rounded-full bg-[#c9a961]" style={{ width: `${Math.max(0, Math.min(100, j.pct_complete))}%` }} /></div>
                  <div className="mt-1 text-[10px] text-[#1e3d3a]/45">{j.pct_complete}% complete</div>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
                    <div><span className="text-[#1e3d3a]/40">Contract</span><div className="tabular-nums">{money(j.contract)}</div></div>
                    <div><span className="text-[#1e3d3a]/40">Spent</span><div className="tabular-nums">{money(j.spent)}</div></div>
                    <div><span className="text-[#1e3d3a]/40">Billed</span><div className="tabular-nums">{money(j.billed)}</div></div>
                    <div><span className="text-[#1e3d3a]/40">Received</span><div className="tabular-nums">{money(j.received)}</div></div>
                  </div>
                  {unc > 0 && <div className="mt-2 text-[11px] text-[#b23a48]">Uncollected: {money(unc)}</div>}
                </div>
              );
            })}
          </div>
        ) : <p className="text-sm text-[#1e3d3a]/50 py-8 text-center">No jobs yet. Add a project you&apos;re under contract for to track budget, billing and collections.</p>}
      </Card>

      {/* Expense modal */}
      {expForm && (
        <Modal title={expForm.id ? "Edit entry" : "Add entry"} onClose={() => setExpForm(null)} onSave={saveExpense} busy={busy} testid="pf-exp-modal">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type"><select data-testid="pf-exp-kind" value={expForm.kind} onChange={(e) => setExpForm({ ...expForm, kind: e.target.value })} className={inputCls}><option value="expense">Expense</option><option value="income">Income</option></select></Field>
            <Field label="Date"><input data-testid="pf-exp-date" type="date" value={expForm.date} onChange={(e) => setExpForm({ ...expForm, date: e.target.value })} className={inputCls} /></Field>
            <Field label="Category"><select data-testid="pf-exp-category" value={expForm.category} onChange={(e) => setExpForm({ ...expForm, category: e.target.value })} className={inputCls}>{data.categories.map((c) => <option key={c} value={c}>{c}</option>)}</select></Field>
            <Field label="Amount ($)"><input data-testid="pf-exp-amount" type="number" min="0" step="0.01" value={expForm.amount} onChange={(e) => setExpForm({ ...expForm, amount: e.target.value })} className={inputCls} /></Field>
            <Field label="Vendor / source" full><input data-testid="pf-exp-vendor" value={expForm.vendor} onChange={(e) => setExpForm({ ...expForm, vendor: e.target.value })} className={inputCls} placeholder="e.g. Home Depot" /></Field>
            <Field label="Note" full><input data-testid="pf-exp-note" value={expForm.note} onChange={(e) => setExpForm({ ...expForm, note: e.target.value })} className={inputCls} /></Field>
          </div>
        </Modal>
      )}

      {/* Job modal */}
      {jobForm && (
        <Modal title={jobForm.id ? "Edit job" : "Add job"} onClose={() => setJobForm(null)} onSave={saveJob} busy={busy} testid="pf-job-modal">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Job name" full><input data-testid="pf-job-name" value={jobForm.name} onChange={(e) => setJobForm({ ...jobForm, name: e.target.value })} className={inputCls} placeholder="e.g. Maple Ridge — Lot 12" /></Field>
            {[["contract", "Contract value"], ["budget", "Budget cost"], ["spent", "Spent to date"], ["pct_complete", "% complete"], ["billed", "Billed"], ["received", "Received"]].map(([k, lbl]) => (
              <Field key={k} label={lbl}><input data-testid={`pf-job-${k}`} type="number" min="0" step="0.01" value={jobForm[k]} onChange={(e) => setJobForm({ ...jobForm, [k]: e.target.value })} className={inputCls} /></Field>
            ))}
            <Field label="Status" full><input data-testid="pf-job-status" value={jobForm.status} onChange={(e) => setJobForm({ ...jobForm, status: e.target.value })} className={inputCls} placeholder="e.g. Framing" /></Field>
          </div>
        </Modal>
      )}
    </div>
  );
}

const inputCls = "w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm text-[#1e3d3a] focus:border-[#c9a961] focus:outline-none";

const Field = ({ label, children, full }) => (
  <div className={full ? "col-span-2" : ""}>
    <label className="block text-[10px] uppercase tracking-wide text-[#1e3d3a]/50 mb-1">{label}</label>
    {children}
  </div>
);

const Modal = ({ title, children, onClose, onSave, busy, testid }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid={testid} onClick={onClose}>
    <div className="w-full max-w-md rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-head text-xl text-[#1e3d3a]">{title}</h3>
        <button data-testid="pf-modal-close" onClick={onClose} className="text-[#1e3d3a]/40 hover:text-[#1e3d3a]"><X className="h-5 w-5" /></button>
      </div>
      {children}
      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onClose} className="rounded-full border border-[#1e3d3a]/20 px-4 py-2 text-sm text-[#1e3d3a]/70 hover:border-[#1e3d3a]/40">Cancel</button>
        <button data-testid="pf-modal-save" disabled={busy} onClick={onSave} className="inline-flex items-center gap-1.5 rounded-full bg-[#c9a961] px-4 py-2 text-sm font-semibold text-[#1a2e2b] hover:bg-[#d8b972] disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
        </button>
      </div>
    </div>
  </div>
);
